import {
  CountryList,
  FieldSets,
  OpportunityStage,
} from "client/lib/apt-merchant";
import type {
  RestoreResponse,
  StoreLocationsFile,
  UserStateResponse,
} from "client/types";
import { apiClient } from "client/utils/api-client";
import { CashPayFields, CustomerSupportFields } from "client/utils/transforms";

export type StoreData = { data: UpdateStoreInfoRequest };

export type UpdateStoreInfoRequest = FieldSets.CompanyInformationFieldSetValueMap &
  FieldSets.StoreDetailsFieldSetValueMap & {
    locations: Partial<FieldSets.StoreLocationValueMap>[];
    owners: Partial<FieldSets.BusinessOwnerValueMap>[];
    storeLocationsFile?: StoreLocationsFile["path"];
  };

export type SubmitCustomerSupportInfoRequest = {
  [key in CustomerSupportFields]?: any;
};

export type UserStateAndRestoreResponse = UserStateResponse & {
  restoreResponse: RestoreResponse;
};

export const getUserState: () => Promise<UserStateResponse> = () =>
  apiClient<UserStateResponse>("/user-state");

export const restoreUser: () => Promise<RestoreResponse> = () =>
  apiClient<RestoreResponse>("/api/restore");

export const getUserStateAndRestore: () => Promise<UserStateAndRestoreResponse> = async () => {
  const restoreResponse = await restoreUser();
  const { userState, formCompletedState } = await getUserState();
  return {
    userState,
    formCompletedState,
    restoreResponse,
  };
};

export type OpportunityStageResponse = {
  opportunityStage: OpportunityStage;
};

export const getOpportunityStage: () => Promise<OpportunityStageResponse> = () =>
  apiClient<OpportunityStageResponse>("/api/opportunity-stage");

export const saveMerchant: (data: any) => Promise<Record<string, never>> = (
  data
) => apiClient<Record<string, never>>("/api/save", { data });

export const signupMerchant: (data: any) => Promise<UserStateResponse> = (
  data: any
) => apiClient<UserStateResponse>("/signup", { data: { data } });

export const completeCustomerServiceForm = (data: {
  data: SubmitCustomerSupportInfoRequest;
}): Promise<UserStateResponse> =>
  apiClient<UserStateResponse>("/api/update-customer-service-info", {
    data,
  });

export const updateGatewayEnabled: (
  isEnabled: boolean
) => Promise<UserStateResponse> = async (isEnabled: boolean) =>
  apiClient<UserStateResponse>("/api/zdo/update-gateway-enabled", {
    data: {
      isEnabled,
    },
  });

export const updateStoreInfo = (data: StoreData): Promise<UserStateResponse> =>
  apiClient<UserStateResponse>("/api/update-store-info", { data });

export type OnboardingLinkResponse = {
  onboardingLink: string;
};

export type GetCashPayEligibilityResponse = { eligible: boolean };

export const checkCashPayEligibility = (): Promise<GetCashPayEligibilityResponse> =>
  apiClient<GetCashPayEligibilityResponse>("/api/check-cashpay-eligibility");

export type SubmitCashPayInfoRequest = {
  [key in CashPayFields]?: any;
};

export const updateCashPayInfo = async (data: {
  data: SubmitCashPayInfoRequest;
}): Promise<UserStateResponse> =>
  apiClient<UserStateResponse>("/api/update-cashpay-info", {
    data,
  });

export const updateCashPayProfileImageUrl = async (data: {
  data: SubmitCashPayInfoRequest;
}): Promise<{
  success?: boolean;
  error?: string;
}> =>
  apiClient<{ success?: boolean; error?: string }>(
    "/api/update-cashpay-profile-image-url",
    {
      data,
    }
  );

export const updateCashpayChannel = async (
  mlsProspectRef?: string
): Promise<{ success?: boolean; error?: string }> =>
  apiClient<{ success?: boolean; error?: string }>(
    "/api/update-cashpay-channel",
    {
      data: { mlsProspectRef },
    }
  );

export const generateOnboardingLink = (
  prospectId: string
): Promise<OnboardingLinkResponse> =>
  apiClient<OnboardingLinkResponse>("/api/onboarding-link", {
    data: { prospectId },
  });

export const verifyCaptcha = (
  token: string | null
): Promise<{ success: boolean }> =>
  apiClient<{ success: boolean }>("/check-captcha-token", {
    data: {
      token,
    },
  });

export type CompleteContactUsFormParams = {
  contentBody: string;
  file?: File;
  country?: CountryList;
  email: string;
  storeName?: string;
  currentPage: "Onboarding Page" | "Status Page";
};

function buildFormData(data: CompleteContactUsFormParams): FormData {
  const postData = new FormData();
  if (data.file) {
    postData.append("file", data.file);
  }
  if (data.country) {
    postData.append("country", data.country);
  }
  if (data.storeName) {
    postData.append("storeName", data.storeName);
  }
  postData.append("contentBody", data.contentBody);
  postData.append("email", data.email);
  postData.append("currentPage", data.currentPage);
  return postData;
}

export const completeContactUsForm = async (
  data: CompleteContactUsFormParams
): Promise<{ message: string }> => {
  const formData = buildFormData(data);
  return apiClient<{ message: string }>(
    "/send-merchant-admin-help-desk-email",
    {
      config: {
        method: "POST",
        body: formData,
      },
    }
  );
};
